import { Box, CircularProgress, Divider, Typography } from '@mui/material'
import "@fontsource/rubik";
import CustomButton from '../../../components/CustomButton'
import { revisionHistoryErrorMessage } from '../../../utils/revisionHistory'
import RequestLog from './RequestLog'
import RevisionHistoryNotes from './RevisionHistoryNotes'
import RevisionHistoryTimeline from './RevisionHistoryTimeline'
import RevisionVersionActions from './RevisionVersionActions'
import { contentCountLabel } from '../../../utils/revisionContent'

/**
 * The notes column of the review card. It is always notes - what changes is
 * whose notes, and whether they can be edited.
 *
 *  - Current cut with an open revision -> the live RequestLog, exactly as before.
 *    Notes are written here and the player timestamps line up.
 *  - Anything else (a superseded cut, or a revision already submitted) -> the
 *    read-only notes and activity the history endpoint returned for that version.
 *
 * `showLiveLog` is decided by the card, which also guarantees it is only ever
 * true while the current cut is selected - so the editable input is never shown
 * against a historic video.
 */
const RevisionNotesPanel = (props) => {
  const {
    version, showLiveLog, isCurrentCut, notesVisible,
    isLoading, error, onBackToCurrent, retry,
  } = props;

  const header = (
    <Box sx={{ mb: 1 }}>
      <Box className='flex a-center' gap={1} sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Typography
          sx={{ fontWeight: 600, fontFamily: 'Rubik', color: 'black', fontSize: { xs: '10px', md: '12px', lg: '13px', xl: '18px' } }}
        >
          {version ? `Notes · Version ${version?.version}` : 'Revisions Log'}
        </Typography>
        {/* What the editor received with this round, so the notes are not read
            as the whole of the request. */}
        {Number(version?.revision?.content_counts?.total) > 0 && (
          <Typography
            data-testid='revision-notes-file-count'
            sx={{ fontFamily: 'Rubik', opacity: 0.7, fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '12px' } }}
          >
            {contentCountLabel(version.revision.content_counts)}
          </Typography>
        )}
        <RevisionVersionActions version={version} compact />
      </Box>

      {/* Say plainly why the input is gone, and offer the way back. */}
      {!!version && !isCurrentCut && (
        <Box data-testid='revision-notes-readonly-hint' sx={{ mt: 0.4 }}>
          <Typography
            sx={{ fontFamily: 'Rubik', fontStyle: 'italic', color: '#08231B', opacity: 0.7, fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '12px' } }}
          >
            Read-only — this is an older cut
          </Typography>
          <Typography
            onClick={onBackToCurrent}
            sx={{
              fontFamily: 'Rubik', fontWeight: 600, color: '#08231B', cursor: 'pointer', textDecoration: 'underline',
              fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '12px' },
            }}
          >
            Back to the current version
          </Typography>
        </Box>
      )}
    </Box>
  );

  // The live log owns its own heading area and input; it needs the full column.
  if (showLiveLog) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <RequestLog />
      </Box>
    )
  }

  return (
    <Box
      data-testid='revision-notes-panel'
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '13px', overflow: 'hidden' }}
    >
      {header}

      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', paddingRight: '4px' }}>
        {isLoading ? (
          <Box data-testid='revision-notes-loading' className='flex j-center a-center' sx={{ height: '100%' }}>
            <CircularProgress size={22} />
          </Box>
        ) : error ? (
          <Box data-testid='revision-notes-error' className='flex col j-center a-center' gap={1.5} sx={{ height: '100%' }}>
            <Typography
              sx={{ fontFamily: 'Rubik', color: '#08231B', textAlign: 'center', fontSize: { xs: '8px', md: '10px', lg: '10px', xl: '13px' } }}
            >
              {revisionHistoryErrorMessage(error?.http_code, error?.message)}
            </Typography>
            {/* 403 and 404 are terminal for this project - only retry what a retry can fix. */}
            {error?.http_code !== 403 && error?.http_code !== 404 && (
              <CustomButton
                btnText='Retry'
                data-testid='revision-notes-retry'
                onClick={retry}
                component='button'
                type='button'
                color='secondary'
                variant='contained'
                style={{ borderRadius: '5px', border: 'none', textTransform: 'none' }}
                sx={{ fontFamily: 'Rubik', fontWeight: 600, width: { xs: '64px', xl: '100px' }, height: { xs: '18px', xl: '28px' }, fontSize: { xs: '8px', lg: '9px', xl: '13px' } }}
              />
            )}
          </Box>
        ) : !version ? (
          <Box data-testid='revision-notes-empty' className='flex j-center a-center' sx={{ height: '100%' }}>
            <Typography
              sx={{ fontFamily: 'Rubik', color: '#08231B', textAlign: 'center', fontSize: { xs: '8px', md: '10px', lg: '10px', xl: '13px' } }}
            >
              No versions have been delivered yet
            </Typography>
          </Box>
        ) : (
          <>
            {!version?.video_url && (
              <Typography
                data-testid='revision-notes-processing'
                sx={{ fontFamily: 'Rubik', fontStyle: 'italic', color: '#08231B', opacity: 0.75, fontSize: { xs: '7px', md: '9px', lg: '9px', xl: '12px' }, mb: 1 }}
              >
                Video is processing
              </Typography>
            )}

            {/* Hidden entirely - not emptied - without Notes -> View. */}
            {notesVisible && (
              <>
                <RevisionHistoryNotes revision={version?.revision} />
                <Divider sx={{ my: 1.5 }} />
              </>
            )}

            <RevisionHistoryTimeline timeline={version?.timeline} version={version?.version} />
          </>
        )}
      </Box>
    </Box>
  )
}

export default RevisionNotesPanel
